import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IMAGES } from '../../shared/constant';
import { AgentService, Agent, CreateAgentRequest } from '../services/agent.service';
import { AuthService } from '../auth/auth.service';

@Component({
  selector: 'app-agent-catalog',
  standalone: false,
  templateUrl: './agent-catalog.component.html',
  styleUrl: './agent-catalog.component.css'
})
export class AgentCatalogComponent implements OnInit {

  iconImages = IMAGES;
  allAgents: Agent[] = [];
  filteredAgents: Agent[] = [];
  isLoading = false;
  selectedTabIndex = 0;
  isToggleOn = false;
  agentsImages = ['/assets/images/agent1.svg', '/assets/images/agent2.svg', '/assets/images/agent3.svg'];
  agentImageMap: { [key: number]: string } = {};
  showAgentProfile = false;
  workFlowList = [1,2,3,4,5,6,7];
  duplicatingAgentId: number | null = null;
  isCreatingAgent = false;
  showAuditTrail = false;
  showErrorModal = false;
  errorModalMessage = '';
  isDeletingId: number | null = null;
  showDeleteConfirmModal = false;
  agentToDelete: { id: number; name: string } | null = null;
  showSuccessModal = false;
  successModalMessage = '';

  constructor(
    private agentService: AgentService,
    private router: Router,
    private authService: AuthService
  ) {

  }

  ngOnInit(): void {
    this.loadAgents();
  }

  loadAgents(): void {
    this.isLoading = true;
    this.agentService.getAgents().subscribe({
      next: (response) => {
        // Sort agents by name
        this.allAgents = response.agents.sort((a, b) => {
          return a.Name.localeCompare(b.Name);
        });
    this.agentImageMap = {};
        this.allAgents.forEach(agent => {
      const randomIndex = Math.floor(Math.random() * this.agentsImages.length);
          this.agentImageMap[agent.Id] = this.agentsImages[randomIndex];
    });
        this.filterAgents();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading agents:', error);
        this.isLoading = false;
      }
    });
  }

  filterAgents(): void {
    if (this.selectedTabIndex === 0) {
      // All Agents
      this.filteredAgents = [...this.allAgents];
    } else if (this.selectedTabIndex === 1) {
      // Published Agents (has PublishedBuildId and Status is not DRAFT)
      this.filteredAgents = this.allAgents.filter(agent => 
        agent.PublishedBuildId !== null && agent.Status !== 'DRAFT'
      );
    } else if (this.selectedTabIndex === 2) {
      // Draft Agents (Status is DRAFT)
      this.filteredAgents = this.allAgents.filter(agent => agent.Status === 'DRAFT');
    }
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    this.filterAgents();
  }

  get allAgentsCount(): number {
    return this.allAgents.length;
  }

  get publishedAgentsCount(): number {
    return this.allAgents.filter(agent => 
      agent.PublishedBuildId !== null && agent.Status !== 'DRAFT'
    ).length;
  }

  get draftAgentsCount(): number {
    return this.allAgents.filter(agent => agent.Status === 'DRAFT').length;
  }

  getAgentProfile(): void {
    this.showAgentProfile = true;
  }

  editAgent(agentId: number, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/agent', agentId]);
  }

  isMassCallingAgent(agent: Agent): boolean {
    return agent.Name.toLowerCase().includes('mass agent');
  }

  private getCurrentUser(): string {
    // Get current user from auth service - use email as identifier
    try {
      const userObservable = this.authService.user$();
      const currentUser = (userObservable as any).source?.value || (userObservable as any).value;
      if (currentUser?.userEmail) {
        return currentUser.userEmail;
      }
      
      // Fallback: use subscribe pattern if direct access doesn't work
      let userEmail = '';
      const subscription = this.authService.user$().subscribe(user => {
        userEmail = user?.userEmail || '';
      });
      subscription.unsubscribe();
      return userEmail;
    } catch (error) {
      console.error('Error getting current user:', error);
      return '';
    }
  }

  openAuditTrail(): void {
    this.showAuditTrail = true;
  }

  createAgent(): void {
    if (this.isCreatingAgent) {
      return; // Prevent multiple clicks
    }

    this.isCreatingAgent = true;
    const userEmail = this.getCurrentUser();
    
    const payload: CreateAgentRequest = {
      Name: 'New Agent',
      Description: '',
      AgentType: 'Prescreening',
      CreatedBy: userEmail
    };

    this.agentService.createAgent(payload).subscribe({
      next: (response) => {
        if (response.success && response.agent) {
          // Reload agents to show the new agent
          this.loadAgents();
          // Navigate to the newly created agent
          this.router.navigate(['/agent', response.agent.Id]);
        }
        this.isCreatingAgent = false;
      },
      error: (error) => {
        console.error('Error creating agent:', error);
        let errorMessage = 'Failed to create agent. Please try again.';
        
        if (error?.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        }
        
        this.showErrorModalMessage(errorMessage);
        this.isCreatingAgent = false;
      }
    });
  }

  duplicateAgent(agentId: number, event: Event): void {
    event.stopPropagation();
    
    // Set loading state
    this.duplicatingAgentId = agentId;
    
    this.agentService.duplicateAgent(agentId).subscribe({
      next: (response) => {
        if (response.success && response.agent) {
          // Reload agents to show the new duplicated agent
          this.loadAgents();
          // Navigate to the newly created agent
          this.router.navigate(['/agent', response.agent.Id]);
        }
        // Clear loading state
        this.duplicatingAgentId = null;
      },
      error: (error) => {
        console.error('Error duplicating agent:', error);
        let errorMessage = 'Failed to duplicate agent. Please try again.';
        
        if (error?.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        }
        
        // Show error message in modal
        this.showErrorModalMessage(errorMessage);
        // Clear loading state
        this.duplicatingAgentId = null;
      }
    });
  }

  isDuplicating(agentId: number): boolean {
    return this.duplicatingAgentId === agentId;
  }

  showErrorModalMessage(message: string): void {
    this.errorModalMessage = message;
    this.showErrorModal = true;
  }

  closeErrorModal(): void {
    this.showErrorModal = false;
    this.errorModalMessage = '';
  }

  openDeleteConfirm(agent: Agent, event: Event): void {
    event.stopPropagation();
    this.agentToDelete = { id: agent.Id, name: agent.Name };
    this.showDeleteConfirmModal = true;
  }

  closeDeleteConfirmModal(): void {
    this.showDeleteConfirmModal = false;
    this.agentToDelete = null;
  }

  confirmDelete(): void {
    if (!this.agentToDelete) return;

    const { id, name } = this.agentToDelete;
    this.isDeletingId = id;
    this.closeDeleteConfirmModal();

    this.agentService.checkPendingCalls(id).subscribe({
      next: (res) => {
        if (res.hasPendingCalls) {
          this.isDeletingId = null;
          this.showErrorModalMessage(`Calls are currently in queue for agent "${name}". Please try again later.`);
          return;
        }
        if (res.isAssignedToPublishedAIPool) {
          this.isDeletingId = null;
          const poolList = res.assignedPoolNames && res.assignedPoolNames.length > 0
            ? `\n\nAssigned pools:\n• ${res.assignedPoolNames.join('\n• ')}`
            : '';
          this.showErrorModalMessage(`Agent "${name}" is currently assigned to one or more published AI pools. Please unassign the agent before deleting.${poolList}`);
          return;
        }

        this.agentService.deleteAgent(id).subscribe({
          next: () => {
            this.allAgents = this.allAgents.filter(a => a.Id !== id);
            this.filterAgents();
            this.isDeletingId = null;
            this.successModalMessage = `Agent "${name}" has been deleted successfully.`;
            this.showSuccessModal = true;
          },
          error: (error) => {
            console.error('Error deleting agent:', error);
            this.isDeletingId = null;
            const msg = error?.error?.detail || error?.error?.message || 'Failed to delete agent. Please try again.';
            this.showErrorModalMessage(msg);
          }
        });
      },
      error: (error) => {
        console.error('Error checking pending calls:', error);
        this.isDeletingId = null;
        this.showErrorModalMessage(`Unable to verify agent status at this time. Deletion has been blocked. Please try again later.`);
      }
    });
  }

  closeSuccessModal(): void {
    this.showSuccessModal = false;
    this.successModalMessage = '';
  }

}
